import {useTranslation} from "react-i18next";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import NarrowSecureLayout from "../../../layout/NarrowSecureLayout.jsx";
import LoadingComponent from "../../../components/LoadingComponent.jsx";
import InfoBox from "../../../components/InfoBox.jsx";
import {useErrorTranslation} from "../../../api/utilities/translateErrorMessage.js";
import {ProjectApi, StatusCategory, StatusChange} from "../../../api/authority/generated/index.ts";
import toast from "react-hot-toast";
import ContentBlock from "../../../components/ContentBlock.jsx";
import Select from "react-select";

export default function OrderStatusSettings({project}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let {getAuthorityConfiguration} = useContext(GlobalContext);

    let [statusSettings, setStatusSettings] = useState(null);
    let [updating, setUpdating] = useState(false);
    let [refresh, setRefresh] = useState(false);

    useEffect(() => {
        if (refresh) {
            setRefresh(false);
            return;
        }

        setUpdating(true);

        new ProjectApi(getAuthorityConfiguration())
            .orderStatusList({
                projectId: project.id,
            })
            .then((data) => {
                setUpdating(false);
                setStatusSettings(data);
            })
            .catch(e => et(e).then(toast.error))
            .finally(() => setUpdating(false));

    }, [project.id, refresh]);

    let statusLists = useMemo(
        () => {
            let statusLists = {
                completedPossible: [],
                completed: [],
                receivedPossible: [],
                received: [],
                cancelledPossible: [],
                cancelled: [],
            };

            let isStatusCompleted = (status) => statusSettings.completed.filter(
                (completedStatus) => completedStatus.value === status.value
            ).length > 0;

            let isStatusReceived = (status) => statusSettings.received.filter(
                (receivedStatus) => receivedStatus.value === status.value
            ).length > 0;

            let isStatusCancelled = (status) => statusSettings.cancelled.filter(
                (cancelledStatus) => cancelledStatus.value === status.value
            ).length > 0;

            if (statusSettings) {
                for (let status of statusSettings.all) {
                    let isCompleted = isStatusCompleted(status);
                    let isReceived = isStatusReceived(status);
                    let isCancelled = isStatusCancelled(status);

                    if (isCompleted) {
                        statusLists.completed.push(status);
                        statusLists.completedPossible.push(status);
                    }

                    if (isReceived) {
                        statusLists.received.push(status);
                        statusLists.receivedPossible.push(status);
                    }

                    if (isCancelled) {
                        statusLists.cancelled.push(status);
                        statusLists.cancelledPossible.push(status);
                    }

                    if (!isCompleted && !isReceived && !isCancelled) {
                        statusLists.completedPossible.push(status);
                        statusLists.receivedPossible.push(status);
                        statusLists.cancelledPossible.push(status);
                    }
                }
            }

            return statusLists;
        }, [statusSettings]
    );

    const updateStatus = (category, status, statusId) => {
        setUpdating(true);

        new ProjectApi(getAuthorityConfiguration())
            .modifyOrderStatusList({
                projectId: project.id,
                projectStatusUpdate: {
                    statusId: statusId,
                    category: category,
                    change: status,
                }
            })
            .catch(e => et(e).then(toast.error))
            .finally(() => setRefresh(true));
    };

    return (
        <NarrowSecureLayout title={t('project.order-statuses')}>
            <LoadingComponent loading={statusSettings === null || updating}>
                <ContentBlock
                    title={t('project.statuses.completed')}
                    tooltip={t('project.statuses.completed-description')}
                >
                    <Select
                        options={Object.values(statusLists?.completedPossible ?? {})}
                        value={statusLists.completed}
                        onChange={(_, change) => {
                            if (change.action === 'select-option') {
                                updateStatus(StatusCategory.Completed, StatusChange.Add, change.option.value);
                            }

                            if (change.action === 'remove-value') {
                                updateStatus(StatusCategory.Completed, StatusChange.Remove, change.removedValue.value);
                            }
                        }}
                        isMulti
                        isClearable={false}
                    />
                </ContentBlock>

                <ContentBlock
                    title={t('project.statuses.received')}
                    tooltip={t('project.statuses.received-description')}
                >
                    <Select
                        options={Object.values(statusLists?.receivedPossible ?? {})}
                        value={statusLists.received}
                        onChange={(_, change) => {
                            if (change.action === 'select-option') {
                                updateStatus(StatusCategory.Received, StatusChange.Add, change.option.value);
                            }

                            if (change.action === 'remove-value') {
                                updateStatus(StatusCategory.Received, StatusChange.Remove, change.removedValue.value);
                            }
                        }}
                        isMulti
                        isClearable={false}
                    />
                </ContentBlock>

                <ContentBlock
                    title={t('project.statuses.cancelled')}
                    tooltip={t('project.statuses.cancelled-description')}
                >
                    <Select
                        options={Object.values(statusLists?.cancelledPossible ?? {})}
                        value={statusLists.cancelled}
                        onChange={(_, change) => {
                            if (change.action === 'select-option') {
                                updateStatus(StatusCategory.Cancelled, StatusChange.Add, change.option.value);
                            }

                            if (change.action === 'remove-value') {
                                updateStatus(StatusCategory.Cancelled, StatusChange.Remove, change.removedValue.value);
                            }
                        }}
                        isMulti
                        isClearable={false}
                    />
                </ContentBlock>

            </LoadingComponent>
            <div>
                <InfoBox template={'inline'} type={'warning'}>
                    {t('project.statuses.not-seeing-means-not-yet-downloaded')}
                </InfoBox>
            </div>
            <div>
                <InfoBox template={'inline'} type={'warning'}>
                    {t('project.statuses.statuses-are-not-applied-immediately')}
                </InfoBox>
            </div>
        </NarrowSecureLayout>
    );
}
